import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { NavBar } from '@/components/NavBar';
import axios from 'axios';
import { Check, Star, CreditCard, Calendar, ExternalLink, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MONTHLY_FEATURES = [
  'Full access to all 42 country profiles',
  'Real-time alerts and daily digest',
  'Unlimited reports',
  'Up to 10 users',
  'Email support',
  'Onboarding session included',
];

const ANNUAL_FEATURES = [
  'Everything in Monthly',
  'Priority support',
  'Quarterly business review',
  'Custom report templates',
  'Dedicated customer success manager',
  'SSO and advanced admin controls available on request',
];

export default function BillingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/billing/subscription`).then(r => {
      setSubscription(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleSubscribe = async (plan) => {
    setCheckoutLoading(plan);
    try {
      const { data } = await axios.post(`${API}/billing/checkout-session`, {
        plan,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create checkout session');
      setCheckoutLoading(null);
    }
  };

  const handleManageBilling = async () => {
    setPortalLoading(true);
    try {
      const { data } = await axios.post(`${API}/billing/portal-session`);
      window.location.href = data.url;
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to open billing portal');
      setPortalLoading(false);
    }
  };

  const isActive = subscription && ['active', 'trialing'].includes(subscription.status);

  return (
    <div className="min-h-screen bg-[#F8FAFB]" data-testid="billing-page">
      <NavBar />
      <div className="max-w-5xl mx-auto px-6 pt-24 pb-12">
        <h1 className="text-2xl font-bold text-[#0A2533] mb-2" style={{ fontFamily: "'Trebuchet MS', sans-serif" }}>Billing</h1>
        <p className="text-sm text-[#4E6974] mb-8">Manage your TradePath subscription</p>

        {loading ? (
          <div className="flex items-center gap-2 text-[#4E6974]"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</div>
        ) : isActive ? (
          /* Active subscription card */
          <div className="bg-white rounded-xl border border-[#E8ECF0] p-6 max-w-lg" data-testid="billing-active-card">
            <div className="flex items-center gap-2 mb-4">
              <CreditCard className="h-5 w-5 text-[#0097A7]" />
              <h2 className="text-lg font-bold text-[#0A2533]">Current subscription</h2>
            </div>
            <div className="space-y-3 mb-6">
              <div className="flex justify-between">
                <span className="text-sm text-[#4E6974]">Plan</span>
                <span className="text-sm font-medium text-[#0A2533] capitalize">{subscription.plan}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-[#4E6974]">Status</span>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${subscription.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                  {subscription.status}
                </span>
              </div>
              {subscription.current_period_end && (
                <div className="flex justify-between">
                  <span className="text-sm text-[#4E6974]">Renews</span>
                  <span className="text-sm text-[#0A2533]">{new Date(subscription.current_period_end).toLocaleDateString()}</span>
                </div>
              )}
              {subscription.cancel_at_period_end && (
                <div className="bg-yellow-50 text-yellow-700 text-xs rounded-lg p-2">Cancellation scheduled at end of current period</div>
              )}
            </div>
            <button onClick={handleManageBilling} disabled={portalLoading} data-testid="billing-manage-btn"
              className="flex items-center gap-2 px-5 py-2.5 bg-[#00707C] hover:bg-[#005F6A] disabled:bg-[#B0BEC5] text-white text-sm font-medium rounded-lg transition-colors">
              {portalLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
              Manage billing
            </button>
          </div>
        ) : (
          /* Plan selection */
          <>
            <p className="text-[#4E6974] text-sm mb-6">Choose a plan to access the TradePath Explore Dashboard and all features.</p>
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl" data-testid="billing-plans">
              {/* Monthly */}
              <div className="bg-white border border-[#E0E0E0] rounded-2xl p-8 flex flex-col" data-testid="billing-plan-monthly">
                <h3 className="text-xl font-bold text-[#0A2533] mb-2" style={{ fontFamily: "'Trebuchet MS', sans-serif" }}>Monthly</h3>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-4xl font-bold text-[#0A2533]">$2,500</span>
                  <span className="text-[#4E6974] text-sm">AUD/month + GST</span>
                </div>
                <p className="text-sm text-[#4E6974] mb-6">Billed monthly, cancel any time</p>
                <ul className="space-y-3 mb-8 flex-1">
                  {MONTHLY_FEATURES.map(f => (
                    <li key={f} className="flex items-start gap-2 text-sm text-[#0A2533]">
                      <Check className="h-4 w-4 text-[#0097A7] mt-0.5 flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <button onClick={() => handleSubscribe('monthly')} disabled={!!checkoutLoading} data-testid="billing-subscribe-monthly"
                  className="w-full py-3 bg-[#0D3B4F] hover:bg-[#0A2533] disabled:bg-[#B0BEC5] text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2">
                  {checkoutLoading === 'monthly' ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Subscribe monthly
                </button>
              </div>

              {/* Annual */}
              <div className="relative bg-white border-2 border-[#0097A7] rounded-2xl p-8 flex flex-col shadow-lg" data-testid="billing-plan-annual">
                <div className="absolute -top-3 right-6 bg-[#00707C] text-white text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1">
                  <Star className="h-3 w-3" /> Save ~17%
                </div>
                <h3 className="text-xl font-bold text-[#0A2533] mb-2" style={{ fontFamily: "'Trebuchet MS', sans-serif" }}>Annual</h3>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-4xl font-bold text-[#0A2533]">$25,000</span>
                  <span className="text-[#4E6974] text-sm">AUD/year + GST</span>
                </div>
                <p className="text-sm text-[#4E6974] mb-6">Billed annually, paid upfront. Save ~17% versus monthly.</p>
                <ul className="space-y-3 mb-8 flex-1">
                  {ANNUAL_FEATURES.map(f => (
                    <li key={f} className="flex items-start gap-2 text-sm text-[#0A2533]">
                      <Check className="h-4 w-4 text-[#0097A7] mt-0.5 flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <button onClick={() => handleSubscribe('annual')} disabled={!!checkoutLoading} data-testid="billing-subscribe-annual"
                  className="w-full py-3 bg-[#00707C] hover:bg-[#005F6A] disabled:bg-[#B0BEC5] text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2">
                  {checkoutLoading === 'annual' ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Subscribe annually
                </button>
              </div>
            </div>
            <p className="text-xs text-[#4E6974] mt-6 max-w-2xl">
              Prices in AUD, GST exclusive. Both plans include access for your whole organisation.
              Need more than 10 users, on-premises deployment, or enterprise procurement?{' '}
              <a href="mailto:info@nuvantasolutions.com.au" className="text-[#0097A7] hover:underline">Contact us for a custom quote</a>.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
